import type { FirmData, PracticeArea, Office, Article, LeadershipProfile, FirmStatistics } from './firm';
import type { ContactFormData } from './components';

// Validation utility types
export type ValidationResult = {
  isValid: boolean;
  errors: string[];
};

// Validation functions for data models
export const validateFirmStatistics = (stats: Partial<FirmStatistics>): ValidationResult => {
  const errors: string[] = [];

  if (!stats.yearsOfPractice || stats.yearsOfPractice < 0) {
    errors.push('Years of practice must be a positive number');
  }

  if (!stats.mattersHandled || stats.mattersHandled < 0) {
    errors.push('Matters handled must be a positive number');
  }

  if (!stats.clientEngagements || stats.clientEngagements < 0) {
    errors.push('Client engagements must be a positive number');
  }

  if (!stats.regionalOffices || stats.regionalOffices < 0) {
    errors.push('Regional offices must be a positive number');
  }

  if (!stats.legalAssociates || stats.legalAssociates < 0) {
    errors.push('Legal associates must be a positive number');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validatePracticeArea = (area: Partial<PracticeArea>): ValidationResult => {
  const errors: string[] = [];

  if (!area.category || area.category.trim().length === 0) {
    errors.push('Practice area category is required');
  }

  if (!area.services || !Array.isArray(area.services) || area.services.length === 0) {
    errors.push('Practice area must have at least one service');
  }

  if (area.services && area.services.some(service => !service || service.trim().length === 0)) {
    errors.push('All services must have valid names');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateOffice = (office: Partial<Office>): ValidationResult => {
  const errors: string[] = [];

  if (!office.type || !['head', 'regional'].includes(office.type)) {
    errors.push('Office type must be either "head" or "regional"');
  }

  if (!office.address || office.address.trim().length === 0) {
    errors.push('Office address is required');
  }

  if (!office.city || office.city.trim().length === 0) {
    errors.push('Office city is required');
  }

  if (!office.state || office.state.trim().length === 0) {
    errors.push('Office state is required');
  }

  if (!office.pinCode || !/^\d{6}$/.test(office.pinCode)) {
    errors.push('Pin code must be a 6-digit number');
  }

  if (office.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(office.email)) {
    errors.push('Invalid email format');
  }

  if (office.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(office.phone.replace(/[\s\-\(\)]/g, ''))) {
    errors.push('Invalid phone number format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateArticle = (article: Partial<Article>): ValidationResult => {
  const errors: string[] = [];

  if (!article.id || article.id.trim().length === 0) {
    errors.push('Article ID is required');
  }

  if (!article.title || article.title.trim().length === 0) {
    errors.push('Article title is required');
  }

  if (!article.excerpt || article.excerpt.trim().length === 0) {
    errors.push('Article excerpt is required');
  }

  if (!article.category || article.category.trim().length === 0) {
    errors.push('Article category is required');
  }

  if (!article.publishDate || isNaN(Date.parse(article.publishDate))) {
    errors.push('Valid publish date is required');
  }

  if (!article.slug || article.slug.trim().length === 0) {
    errors.push('Article slug is required');
  }

  if (article.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(article.slug)) {
    errors.push('Article slug must be in kebab-case format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateLeadershipProfile = (profile: Partial<LeadershipProfile>): ValidationResult => {
  const errors: string[] = [];

  if (!profile.name || profile.name.trim().length === 0) {
    errors.push('Leadership profile name is required');
  }

  if (!profile.title || profile.title.trim().length === 0) {
    errors.push('Leadership profile title is required');
  }

  if (!profile.biography || profile.biography.trim().length === 0) {
    errors.push('Leadership profile biography is required');
  }

  if (!profile.credentials || !Array.isArray(profile.credentials) || profile.credentials.length === 0) {
    errors.push('Leadership profile must have at least one credential');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateFirmData = (firmData: Partial<FirmData>): ValidationResult => {
  const errors: string[] = [];

  if (!firmData.name || firmData.name.trim().length === 0) {
    errors.push('Firm name is required');
  }

  if (!firmData.tagline || firmData.tagline.trim().length === 0) {
    errors.push('Firm tagline is required');
  }

  if (!firmData.foundingYear || firmData.foundingYear < 1900 || firmData.foundingYear > new Date().getFullYear()) {
    errors.push('Valid founding year is required');
  }

  if (firmData.statistics) {
    const statsValidation = validateFirmStatistics(firmData.statistics);
    if (!statsValidation.isValid) {
      errors.push(...statsValidation.errors);
    }
  }

  if (firmData.offices) {
    firmData.offices.forEach((office, index) => {
      const officeValidation = validateOffice(office);
      if (!officeValidation.isValid) {
        errors.push(...officeValidation.errors.map(error => `Office ${index + 1}: ${error}`));
      }
    });
  }

  if (firmData.practiceAreas) {
    firmData.practiceAreas.forEach((area, index) => {
      const areaValidation = validatePracticeArea(area);
      if (!areaValidation.isValid) {
        errors.push(...areaValidation.errors.map(error => `Practice Area ${index + 1}: ${error}`));
      }
    });
  }

  if (firmData.leadership) {
    firmData.leadership.forEach((profile, index) => {
      const profileValidation = validateLeadershipProfile(profile);
      if (!profileValidation.isValid) {
        errors.push(...profileValidation.errors.map(error => `Leadership Profile ${index + 1}: ${error}`));
      }
    });
  }

  if (firmData.insights) {
    firmData.insights.forEach((article, index) => {
      const articleValidation = validateArticle(article);
      if (!articleValidation.isValid) {
        errors.push(...articleValidation.errors.map(error => `Article ${index + 1}: ${error}`));
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Contact form validation
export const validateContactForm = (formData: Partial<ContactFormData>): ValidationResult => {
  const errors: string[] = [];

  if (!formData.name || formData.name.trim().length === 0) {
    errors.push('Name is required');
  }

  if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.push('Valid email address is required');
  }

  if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
    errors.push('Invalid phone number format');
  }

  if (!formData.subject || formData.subject.trim().length === 0) {
    errors.push('Subject is required');
  }

  if (!formData.message || formData.message.trim().length < 10) {
    errors.push('Message must be at least 10 characters long');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Generic validation utility
export const validateRequired = (value: any, fieldName: string): string | null => {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0)) {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateEmail = (email: string): string | null => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Invalid email format';
  }
  return null;
};

export const validatePhone = (phone: string): string | null => {
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  if (!/^[\+]?[1-9][\d]{0,15}$/.test(cleanPhone)) {
    return 'Invalid phone number format';
  }
  return null;
};

export const validateMinLength = (value: string, minLength: number, fieldName: string): string | null => {
  if (value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters long`;
  }
  return null;
};

export const validateMaxLength = (value: string, maxLength: number, fieldName: string): string | null => {
  if (value.length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters long`;
  }
  return null;
};